// Auth utility functions

// Get current operator ID from localStorage
export const getCurrentOperatorId = () => {
  try {
    const operatorId = localStorage.getItem("operator_id");
    return operatorId ? JSON.parse(operatorId) : null;
  } catch (error) {
    console.error("Error parsing operator ID from localStorage:", error);
    return null;
  }
};

// Check if current user is the same as the given operator ID
export const isCurrentOperator = (operatorId) => {
  const currentOperatorId = getCurrentOperatorId();
  return currentOperatorId === operatorId;
};

// Client (CU) users: read the user type saved at login
const CLIENT_USER_TYPE = "CU";

// Check if the logged-in user is a client user (read-only portal)
export const isClientUser = () => {
  try {
    const userType = localStorage.getItem("user_type");
    return userType ? JSON.parse(userType) === CLIENT_USER_TYPE : false;
  } catch (error) {
    console.error("Error parsing user type from localStorage:", error);
    return false;
  }
};

// Admin-level user types (same as the backend IsAdminUser permission)
const ADMIN_USER_TYPES = ["AD", "SU", "CA"];

// Check if the logged-in user is a CRM admin (e.g. may manage board columns)
export const isAdminUser = () => {
  try {
    const userType = localStorage.getItem("user_type");
    return userType ? ADMIN_USER_TYPES.includes(JSON.parse(userType)) : false;
  } catch (error) {
    console.error("Error parsing user type from localStorage:", error);
    return false;
  }
};

// Get the company ID bound to the logged-in client user (null for operators)
export const getClientCompanyId = () => {
  try {
    const companyId = localStorage.getItem("company_id");
    if (!companyId) return null;
    const parsed = JSON.parse(companyId);
    return parsed ? String(parsed) : null;
  } catch (error) {
    console.error("Error parsing company ID from localStorage:", error);
    return null;
  }
};

// Get every company ID the logged-in client user can read (empty for operators).
// Falls back to the single `company_id` for sessions opened before multi-company access.
export const getClientCompanyIds = () => {
  try {
    const raw = localStorage.getItem("company_ids");
    const parsed = raw ? JSON.parse(raw) : null;
    if (Array.isArray(parsed) && parsed.length > 0) {
      return parsed.filter(Boolean).map(String);
    }
  } catch (error) {
    console.error("Error parsing company IDs from localStorage:", error);
  }
  const companyId = getClientCompanyId();
  return companyId ? [companyId] : [];
};

// Save the companies a client user can read (called at login and when the list is refreshed)
export const setClientCompanyIds = (companyIds) => {
  const ids = Array.isArray(companyIds) ? companyIds.filter(Boolean).map(String) : [];
  localStorage.setItem("company_ids", JSON.stringify(ids));
  // No company left (every access revoked): drop the stale default too, so the route
  // guard sends the client to the 403 page instead of mounting a forbidden board.
  if (!ids.includes(getClientCompanyId())) {
    localStorage.setItem("company_id", JSON.stringify(ids[0] ?? null));
  }
};
